import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import {
  navDestinationFor,
  surfaceForPath,
  trackCtaClick,
  trackNavigationClick,
  type CtaAction,
  type CtaPlacement,
  type CtaPriority,
  type NavPlacement,
} from '@/lib/analytics/funnel';

export interface CtaDescriptor {
  action: CtaAction;
  placement: CtaPlacement;
  priority: CtaPriority;
}

/**
 * Returns a click reporter bound to the page the caller is rendered on.
 *
 * The surface is read from the router rather than passed in, so a shared
 * component — the header, the footer, a guide CTA — reports the page it is
 * currently on without every page having to name itself.
 *
 * The returned function is meant to be used as (or inside) an `onClick`. Do
 * not call it from an effect: an effect can re-run on a re-render and would
 * report a click that never happened.
 */
export function useCtaTracker(): (cta: CtaDescriptor) => void {
  const { pathname } = useLocation();

  return useCallback(
    (cta: CtaDescriptor) => {
      trackCtaClick({ ...cta, surface: surfaceForPath(pathname) });
    },
    [pathname],
  );
}

/**
 * Returns a navigation reporter bound to the page the caller is rendered on.
 *
 * It takes the link's target rather than a destination name, so instrumenting
 * a new chrome link is a matter of adding its path to the map in `funnel.ts`
 * — a target with no entry there reports nothing rather than inventing a name
 * from the URL.
 */
export function useNavTracker(): (nav: { to: string; placement: NavPlacement }) => void {
  const { pathname } = useLocation();

  return useCallback(
    ({ to, placement }: { to: string; placement: NavPlacement }) => {
      const destination = navDestinationFor(to);
      if (!destination) return;
      trackNavigationClick({ destination, placement, surface: surfaceForPath(pathname) });
    },
    [pathname],
  );
}
